import { DeleteForever, Done, Edit, Warning } from "@mui/icons-material";
import { Box, SxProps } from "@mui/material";
import { useStore } from "@priolo/jon";
import ActionsMenu from "components/controls/buttons/ActionsMenu";
import FormParagraph from "components/controls/form/FormParagraph";
import Paragraph from "components/controls/form/Paragraph";
import dayjs from "dayjs";
import { FunctionComponent, useState } from "react";
import attachSo from "stores/attachment";
import farmSo from "stores/farm";
import incidentSo from "stores/incidents";
import theme from "styles/theme";
import { Attachment } from "types/Attachment";
import { CropLot } from "types/CropLot";
import { Cycle } from "types/Cycle";
import { Incident } from "types/Incident";
import ImageBlobCmp from "../photo/ImageBlobCmp";
import LayersPositionSelector from "./LayersPositionSelector";



interface Props {
    incident: Incident
    cycle: Cycle
}

const IncidentCard: FunctionComponent<Props> = ({
    incident,
    cycle,
}) => {

    // STORE
    useStore(incidentSo)

    // HOOKs
    const [collapsed, setCollapsed] = useState(true)

    // HANDLERS
    const handleModify = () => incidentSo.edit(incident)
    const handleRevert = () => incidentSo.revert(incident)
    const handleProceeded = () => incidentSo.proceeded(incident)
    const handleDelete = () => incidentSo.delete(incident)
    const handleCollapse = () => setCollapsed(!collapsed)
    const handlePhotoClick = (attach:Attachment) => {
        attachSo.setSelected(attach)
        attachSo.setDialogOpen(true)
    }

    // RENDER
    const lostTrays = incident.lostTraysPerCropLot?.[0]
    const cropLot: CropLot = cycle?.cropLots?.find(cl => cl.id == lostTrays?.cropLotId)
    const cropLotName = cropLot?.cropName ?? "--"
    const typeLabel = incidentSo.getLabelType(incident.incidentType)
    const total = lostTrays?.numberOfLostTrays ?? 0
    const date = dayjs(incident.dateOfOccurrence).format("DD/MM/YYYY")
    const icon = incident.reverted ? <Done /> : <Warning />
    const flex = 10
    const farmId = farmSo.state.select?.id
    const attachments = incident.attachments

    return (
        <FormParagraph compact key={incident.id}
            onTitleClick={handleModify}
            icon={icon}
            title={cropLotName}
            endRender={<>
                <Box sx={{ ...sxInfoLabel, opacity: 0.7 }}>{date}</Box>
                <ActionsMenu sx={{ color: "inherit" }}
                    actions={[
                        {
                            label: "MODIFY",
                            onClick: handleModify,
                            icon: <Edit />,
                        },
                        (!incident.reverted ?
                            {
                                label: "REVERT",
                                onClick: handleRevert,
                                icon: <Done />,
                            } : {
                                label: "CLEAR REVERTED", color: "secondary",
                                onClick: handleProceeded,
                                icon: <Warning color="secondary" />,
                            }
                        ),
                        { label: "---" },
                        {
                            label: "DELETE", color: "error",
                            onClick: handleDelete,
                            icon: <DeleteForever color="error" />,
                        },
                    ]}
                />
            </>}
        >
            <Box sx={{ display: "flex" }}>

                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>

                    <Box sx={sxInfoRow}>
                        {incident.reverted && <Box sx={sxInfoReverted}>REVERTED</Box>}
                        <Box sx={sxInfoType}>{typeLabel}</Box>
                        <Box sx={sxInfoChip}><span>
                            <span style={sxInfoLabel}>LOST TRAYS: </span>
                            <span style={sxInfoValue}>{total}</span>
                        </span></Box>

                    </Box>

                    <Paragraph title={"LAYERS"} flex={flex}>
                        <LayersPositionSelector sx={{ flex: 1 }}
                            cycle={cycle}
                            layersSelect={incident.positionOfTrolleys}
                        />
                    </Paragraph>

                    {(incident.notes?.length > 0) &&
                        <Paragraph title={"NOTES"} flex={flex} sxLabel={{ alignSelf: 'start', mt: '3px' }}>
                            <Box sx={collapsed ? sxNotesCol : sxNotes}
                                onClick={handleCollapse}
                            >
                                {incident.notes}
                            </Box>
                        </Paragraph>
                    }

                </Box>

                {/* ATTACHMENTS */}
                <Box sx={sxAttachmentImgBox}>
                    {attachments?.map((attach) => (
                        <ImageBlobCmp sx={{ width: 50, height: 50 }}
                            attach={attach}
                            onClick={()=> handlePhotoClick(attach)}
                        />
                    ))}
                </Box>

            </Box>
        </FormParagraph>
    )
}

export default IncidentCard

const sxNotes: SxProps = {
    cursor: "pointer",
    pl: "14px",
    
}

const sxNotesCol: SxProps = {
    ...sxNotes,
    cursor: "pointer",
    flex: 100,
    width: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
}

const sxInfoRow: SxProps = {
    display: "flex",
    gap: "10px",
    alignItems: 'center',
    mb: "12px",
}
const sxInfoChip: SxProps = {
    fontSize: '12px',
    fontWeight: 600,
    borderRadius: '5px',
    padding: '0px 7px',
    minHeight: '22px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.grey.light,
    color: theme.palette.secondary.contrastText,
}
const sxInfoType: SxProps = {
    ...sxInfoChip,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
}
const sxInfoReverted: SxProps = {
    ...sxInfoChip,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
}

const sxInfoLabel: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 400,
}
const sxInfoValue: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 600,
}

const sxAttachmentImgBox: SxProps = {
    display: "flex",
    flex: 1,
    flexWrap: 'wrap',
    gap: 1,
    maxWidth: '300px',
    justifyContent: 'flex-end',
}